import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Search, XCircle } from 'lucide-react';
import { useLocation } from '@/shared/hooks/useLocation';
import { integrationsApi } from '../api/integrationsApi';
import { integrationKeys } from '../queryKeys';
import { Button } from '@/shared/ui/Button';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { PageHeader } from '@/shared/ui';
import styles from './GoogleAdsSetupPage.module.css';

function propertyTypeLabel(siteUrl: string): string {
  return siteUrl.startsWith('sc-domain:') ? 'Domain' : 'URL-prefix';
}

export function GSCSetupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentLocation } = useLocation();
  const tempToken = searchParams.get('t') ?? '';
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [error, setError] = useState('');

  const propertiesQuery = useQuery({
    queryKey: integrationKeys.gscProperties(tempToken),
    queryFn: () => integrationsApi.gsc.getProperties(tempToken).then((r) => r.data.properties),
    enabled: !!tempToken,
  });

  const setupMutation = useMutation({
    mutationFn: () => {
      if (!selectedProperty || !currentLocation)
        throw new Error('No property or location selected');
      return integrationsApi.gsc.selectProperty(currentLocation.id, selectedProperty, tempToken);
    },
    onSuccess: () => navigate('/settings/organic?connected=gsc', { replace: true }),
    onError: (err: unknown) =>
      setError(
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Failed to connect Google Search Console',
      ),
  });

  if (!tempToken) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.errorIcon}>
            <XCircle size={40} strokeWidth={1.5} aria-hidden />
          </div>
          <h1>Invalid Setup Link</h1>
          <p>This link is missing required authorization data.</p>
          <Button onClick={() => navigate('/settings/organic')}>
            Back to Organic & Offline Settings
          </Button>
        </div>
      </div>
    );
  }

  // Try to auto-match a property to the practice website
  const websiteUrl = currentLocation?.name ?? '';
  const suggestedProperty = propertiesQuery.data?.find((p) =>
    p.siteUrl.includes(
      websiteUrl
        .toLowerCase()
        .replace(/^https?:\/\/(www\.)?/, '')
        .split('/')[0],
    ),
  );

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <Search size={40} strokeWidth={1.5} aria-hidden />
        </div>
        <PageHeader
          title="Connect Google Search Console"
          subtitle={
            <>
              Select the property for <strong>{currentLocation?.name ?? 'this location'}</strong>.
            </>
          }
          align="center"
        />

        {propertiesQuery.isLoading && <SkeletonCard />}
        {propertiesQuery.isError && (
          <p className={styles.errorMessage}>
            Failed to load your Search Console properties. Please try again.
          </p>
        )}

        {propertiesQuery.data && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setError('');
              setupMutation.mutate();
            }}
            className={styles.form}
          >
            <div className={styles.pickerList}>
              {propertiesQuery.data.length === 0 && (
                <p className={styles.pickerEmpty}>
                  No Search Console properties found for this Google account.
                </p>
              )}
              {propertiesQuery.data.map((property) => {
                const isSuggested = property.siteUrl === suggestedProperty?.siteUrl;
                return (
                  <label
                    key={property.siteUrl}
                    className={clsx(
                      styles.pickerItem,
                      styles.pickerItemStart,
                      selectedProperty === property.siteUrl && styles.pickerItemActive,
                    )}
                  >
                    <input
                      type="radio"
                      name="property"
                      value={property.siteUrl}
                      checked={selectedProperty === property.siteUrl}
                      onChange={() => setSelectedProperty(property.siteUrl)}
                      className={styles.pickerInput}
                    />
                    <div>
                      <div className={clsx(styles.pickerName, styles.pickerNameRow)}>
                        {property.siteUrl}
                        {isSuggested && <span className={styles.pickerSuggested}>suggested</span>}
                      </div>
                      <div className={styles.pickerMeta}>
                        {propertyTypeLabel(property.siteUrl)} · {property.permissionLevel}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {error && <p className={styles.errorMessage}>{error}</p>}

            <div className={styles.actions}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/settings/organic')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!selectedProperty}
                isLoading={setupMutation.isPending}
              >
                Connect
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
